import { Box, IconButton, Button, Stack, Paper, Tooltip } from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { Logo } from '../Logo/Logo';
import ScreenSlider from './ScreenSlider';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Define dashboard modes
export const DASHBOARD_MODES = {
  STANDARD: 'standard',
  EXECUTIVE: 'executive'
};

interface HeaderProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
  viewMode?: string;
  setViewMode?: (mode: string) => void;
  // Colors for styling
  primaryColor?: string;
  secondaryColor?: string;
  // Filter visibility props
  showFilters?: boolean;
  onToggleFilters?: () => void;
}

export function Header({ 
  isOpen, 
  toggleDrawer, 
  mode = 'light', 
  toggleColorMode,
  viewMode,
  setViewMode,
  primaryColor = '#003087',
  secondaryColor = '#E31837',
  showFilters = true,
  onToggleFilters
}: HeaderProps) {
  const theme = useTheme();
  const location = useLocation();
  
  // Check if we're on the dashboard route
  const isDashboardRoute = location.pathname === '/dashboard' || location.pathname === '/';
  
  // Add zoom state
  const [zoom, setZoom] = useState(80);

  // Apply zoom to document body when zoom changes (only on dashboard route)
  useEffect(() => {
    // Only apply zoom effects on dashboard route
    if (!isDashboardRoute) {
      // Reset zoom effects when not on dashboard
      const mainContent = document.getElementById('main-content');
      const mainContainer = document.querySelector('main');
      if (mainContent && mainContainer) {
        mainContent.style.transform = 'none';
        mainContent.style.width = '100%';
        mainContent.style.height = 'auto';
        mainContainer.style.height = 'auto';
        mainContainer.style.overflow = 'auto';
        mainContainer.style.position = 'relative';
        document.body.style.height = 'auto';
        document.documentElement.style.height = 'auto';
      }
      return;
    }
    
    const zoomLevel = zoom / 100;
    const mainContent = document.getElementById('main-content');
    const mainContainer = document.querySelector('main');
    
    if (mainContent && mainContainer) {
      // If zoom is 100%, reset to default behavior
      if (zoom === 100) {
        mainContent.style.transform = 'none';
        mainContent.style.width = '100%';
        mainContent.style.height = 'auto';
        mainContainer.style.height = 'auto';
        mainContainer.style.overflow = 'auto';
        mainContainer.style.position = 'relative';
        document.body.style.height = 'auto';
        document.documentElement.style.height = 'auto';
        return;
      }
      
      // Apply scaling to the main content
      mainContent.style.transform = `scale(${zoomLevel})`;
      mainContent.style.transformOrigin = 'top left';
      
      // Calculate the adjusted dimensions based on viewport
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const headerHeight = 64; // Header height in pixels
      const availableHeight = viewportHeight - headerHeight;
      
      // Calculate the scaled dimensions
      const scaledWidth = 100 / zoomLevel;
      const scaledHeight = availableHeight / zoomLevel;
      
      // Set the container dimensions to accommodate the scaled content
      mainContent.style.width = `${scaledWidth}%`;
      mainContent.style.height = `${scaledHeight}px`;
      
      // Adjust the main container to fit the scaled content
      mainContainer.style.height = `${scaledHeight}px`;
      mainContainer.style.overflow = 'auto';
      mainContainer.style.position = 'relative';
      
      // Ensure the body and html elements can accommodate the scaled content
      document.body.style.height = `${viewportHeight}px`;
      document.documentElement.style.height = `${viewportHeight}px`;
      
      // Add a resize listener to handle window size changes
      const handleResize = () => {
        const newViewportHeight = window.innerHeight;
        const newAvailableHeight = newViewportHeight - headerHeight;
        const newScaledHeight = newAvailableHeight / zoomLevel;
        
        mainContent.style.height = `${newScaledHeight}px`;
        mainContainer.style.height = `${newScaledHeight}px`;
        document.body.style.height = `${newViewportHeight}px`;
        document.documentElement.style.height = `${newViewportHeight}px`;
      };
      
      window.addEventListener('resize', handleResize);
      
      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [zoom, isDashboardRoute]);

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(8px)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        ml: -2,
      }}>
        <Logo size="small" />
        <IconButton
          onClick={toggleDrawer}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          
          {isOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      
      </Box>
      
      <Box sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        ml: 'auto'
      }}>
       
        {/* Filter Toggle Button - Only show on dashboard route */}
        {isDashboardRoute && onToggleFilters && (
          <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
            <IconButton
              size="small"
              onClick={() => {
                onToggleFilters();
              }}
              sx={{
                display: 'flex',
                color: showFilters ? primaryColor : 'text.secondary',
                backgroundColor: showFilters ? alpha(primaryColor, 0.3) : alpha(primaryColor, 0.1),
                border: `2px solid ${alpha(primaryColor, 0.5)}`,
                borderRadius: 2,
                width: '40px',
                height: '40px',
                '&:hover': {
                  backgroundColor: alpha(primaryColor, 0.4),
                },
              }}
            >
              {showFilters ? <FilterListOffIcon fontSize="medium" /> : <FilterListIcon fontSize="medium" />}
            </IconButton>
          </Tooltip>
        )}
        
        {/* Dashboard View Mode Selector - Only show if viewMode and setViewMode are provided and on dashboard route */}
        {isDashboardRoute && viewMode && setViewMode && (
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <ScreenSlider zoom={zoom} setZoom={setZoom} />
            
            <Paper
              elevation={0}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                p: 0.5,
                border: `1px solid ${alpha(primaryColor, 0.2)}`,
                borderRadius: 2,
                backgroundColor: alpha(primaryColor, 0.05),
              }}
            >
              <Tooltip title="Standard View">
                <IconButton
                  size="small"
                  onClick={() => setViewMode(DASHBOARD_MODES.STANDARD)}
                  sx={{
                    color: viewMode === DASHBOARD_MODES.STANDARD ? primaryColor : 'text.secondary',
                    backgroundColor: viewMode === DASHBOARD_MODES.STANDARD ? alpha(primaryColor, 0.1) : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(primaryColor, 0.1),
                    },
                  }}
                >
                  <ViewDayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Executive Dashboard">
                <IconButton
                  size="small"
                  onClick={() => setViewMode(DASHBOARD_MODES.EXECUTIVE)}
                  sx={{
                    color: viewMode === DASHBOARD_MODES.EXECUTIVE ? secondaryColor : 'text.secondary',
                    backgroundColor: viewMode === DASHBOARD_MODES.EXECUTIVE ? alpha(secondaryColor, 0.1) : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(secondaryColor, 0.1),
                    },
                  }}
                >
                  <DashboardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          </Stack>
        )}
        
        {/* <IconButton
          onClick={toggleColorMode}
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton> */}
      </Box>
    </Box>
  );
} 